/**
 * Host-based routing: storefront vs staff workspace on separate subdomains.
 *
 * Configure per environment via build-time env vars (see .env.staging.example).
 * Production defaults (lewouhmart.com) apply only when hosts/URLs are not set.
 */

/** Production fallbacks — ignored when REACT_APP_* host/URL vars are set. */
const DEFAULT_WORKSPACE_HOST = 'workspace.lewouhmart.com';
const DEFAULT_STOREFRONT_HOSTS = ['lewouhmart.com', 'www.lewouhmart.com'];

function envFlag(name: string): boolean | null {
  const raw = process.env[name]?.trim().toLowerCase();
  if (raw === 'true' || raw === '1' || raw === 'yes') return true;
  if (raw === 'false' || raw === '0' || raw === 'no') return false;
  return null;
}

function hasExplicitHostConfig(): boolean {
  return Boolean(
    process.env.REACT_APP_WORKSPACE_HOST?.trim()
    || process.env.REACT_APP_STOREFRONT_HOSTS?.trim()
    || process.env.REACT_APP_WORKSPACE_URL?.trim()
    || process.env.REACT_APP_STOREFRONT_URL?.trim(),
  );
}

function configuredWorkspaceHost(): string {
  return (process.env.REACT_APP_WORKSPACE_HOST?.trim() || DEFAULT_WORKSPACE_HOST).toLowerCase();
}

function configuredStorefrontHosts(): string[] {
  const raw = process.env.REACT_APP_STOREFRONT_HOSTS?.trim();
  if (!raw) return DEFAULT_STOREFRONT_HOSTS;
  return raw
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
}

export function getHostname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname.toLowerCase();
}

/** True when the browser is on the workspace subdomain. */
export function isWorkspaceHost(hostname = getHostname()): boolean {
  const host = hostname.toLowerCase();
  if (!host || host === 'localhost' || host === '127.0.0.1') {
    return false;
  }
  if (host === configuredWorkspaceHost()) return true;
  const fromUrl = process.env.REACT_APP_WORKSPACE_URL?.trim();
  if (fromUrl) {
    try {
      if (new URL(fromUrl).hostname.toLowerCase() === host) return true;
    } catch {
      // ignore invalid URL
    }
  }
  return false;
}

export function isStorefrontHost(hostname = getHostname()): boolean {
  const host = hostname.toLowerCase();
  if (!host || host === 'localhost' || host === '127.0.0.1') {
    return true;
  }
  if (isWorkspaceHost(host)) return false;
  return configuredStorefrontHosts().includes(host);
}

/**
 * Split routing between storefront and workspace hosts.
 * Set REACT_APP_SUBDOMAIN_ROUTING=true|false to force, or configure host/URL env vars.
 */
export function isSubdomainRoutingEnabled(hostname = getHostname()): boolean {
  const forced = envFlag('REACT_APP_SUBDOMAIN_ROUTING');
  if (forced !== null) return forced;

  const host = hostname.toLowerCase();
  if (!host || host === 'localhost' || host === '127.0.0.1') {
    return hasExplicitHostConfig();
  }

  if (hasExplicitHostConfig()) {
    return isWorkspaceHost(host) || configuredStorefrontHosts().includes(host);
  }

  return isWorkspaceHost(host) || DEFAULT_STOREFRONT_HOSTS.includes(host);
}

export function getStorefrontOrigin(): string {
  const fromEnv = process.env.REACT_APP_STOREFRONT_URL?.trim();
  if (fromEnv) return fromEnv.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && isStorefrontHost()) {
    return window.location.origin;
  }
  const hosts = configuredStorefrontHosts();
  if (hosts.length > 0) {
    return `https://${hosts[0]}`;
  }
  if (typeof window !== 'undefined') return window.location.origin;
  return 'http://localhost:3000';
}

export function getWorkspaceOrigin(): string {
  const fromEnv = process.env.REACT_APP_WORKSPACE_URL?.trim();
  if (fromEnv) return fromEnv.replace(/\/+$/, '');
  if (typeof window !== 'undefined' && isWorkspaceHost()) {
    return window.location.origin;
  }
  return `https://${configuredWorkspaceHost()}`;
}

/** Short hostname label for UI copy (e.g. "test.lewouhmart.com"). */
export function getStorefrontDisplayHost(): string {
  try {
    return new URL(getStorefrontOrigin()).host;
  } catch {
    return configuredStorefrontHosts()[0] || 'storefront';
  }
}

/** Short hostname label for UI copy (e.g. "workspace.test.lewouhmart.com"). */
export function getWorkspaceDisplayHost(): string {
  try {
    return new URL(getWorkspaceOrigin()).host;
  } catch {
    return configuredWorkspaceHost();
  }
}

/** Route prefix for workspace pages: '' on workspace host, '/workspace' on storefront. */
export function getWorkspaceRoutePrefix(): string {
  return isWorkspaceHost() ? '' : '/workspace';
}

/**
 * Path as it should appear on the workspace subdomain (never `/workspace/...`).
 * Accepts canonical `/workspace/...` or host-native `/orders`, `/auth`, etc.
 */
export function workspaceSubdomainPath(path: string): string {
  const trimmed = path.trim();
  if (/^\/workspace\/?$/i.test(trimmed)) return '/';
  if (/^\/workspace\//i.test(trimmed)) {
    return trimmed.replace(/^\/workspace/i, '') || '/';
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

const SHARED_HOST_PATH_PREFIXES = [
  '/auth',
];

function isSharedHostPath(pathname: string): boolean {
  return SHARED_HOST_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** Canonical internal path always uses /workspace/... for permission checks and sidebar rules. */
export function toCanonicalWorkspacePath(pathname: string): string {
  const pathOnly = pathname.split('?')[0].split('#')[0];
  if (/^\/workspace(?:\/|$)/i.test(pathOnly)) {
    return pathOnly;
  }
  if (isWorkspaceHost()) {
    if (pathOnly === '/' || pathOnly === '') return '/workspace';
    if (isSharedHostPath(pathOnly) || isStorefrontOnlyPath(pathOnly)) {
      return pathOnly;
    }
    return `/workspace${pathOnly}`;
  }
  return pathOnly;
}

export function isWorkspacePath(pathname: string): boolean {
  const pathOnly = pathname.split('?')[0].split('#')[0];
  if (isSharedHostPath(pathOnly) || isStorefrontOnlyPath(pathOnly)) {
    return false;
  }
  const canonical = toCanonicalWorkspacePath(pathname);
  return canonical === '/workspace' || canonical.startsWith('/workspace/');
}

/**
 * Path for React Router on the current host.
 * Accepts canonical paths like `/workspace/orders` or host-native `/orders` on workspace host.
 */
export function wsPath(path: string): string {
  const trimmed = path.trim();
  if (isSubdomainRoutingEnabled() && isWorkspaceHost()) {
    return workspaceSubdomainPath(trimmed);
  }
  if (!isWorkspaceHost()) {
    if (/^\/workspace(?:\/|$)/i.test(trimmed)) return trimmed;
    return trimmed.startsWith('/') ? `/workspace${trimmed}` : `/workspace/${trimmed}`;
  }
  return workspaceSubdomainPath(trimmed);
}

export function joinUrl(origin: string, path: string): string {
  const base = origin.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

export function toWorkspaceUrl(path: string): string {
  const normalized = isSubdomainRoutingEnabled()
    ? workspaceSubdomainPath(path)
    : wsPath(path);
  return joinUrl(getWorkspaceOrigin(), normalized);
}

export function toStorefrontUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return joinUrl(getStorefrontOrigin(), normalized);
}

const STOREFRONT_ONLY_PREFIXES = [
  '/cart',
  '/checkout',
  '/account',
  '/departments',
  '/about',
  '/legal',
  '/feedback',
  '/cookie-settings',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/recover-account',
];

/** Paths that belong on the customer storefront host when subdomain routing is enabled. */
export function isStorefrontOnlyPath(pathname: string): boolean {
  const path = pathname.split('?')[0].split('#')[0];
  if (STOREFRONT_ONLY_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
    return true;
  }
  if (path === '/auth' || path.startsWith('/auth/')) {
    if (isWorkspaceHost()) return false;
    return !/^\/auth\/(?:employee|agent)(?:\/|$)/i.test(path);
  }
  const segments = path.split('/').filter(Boolean);
  if (segments.length >= 2) {
    const [, second] = segments;
    if (
      second === 'products'
      || second === 'packages'
      || second === 'cart'
      || second === 'checkout'
    ) {
      return true;
    }
  }
  return false;
}

export function resolveCrossHostRedirect(
  pathname: string,
  search = '',
  hash = '',
): string | null {
  if (!isSubdomainRoutingEnabled()) return null;

  const suffix = `${search}${hash}`;
  const params = new URLSearchParams(search);

  if (isStorefrontHost()) {
    if (pathname === '/workspace' || pathname.startsWith('/workspace/')) {
      return `${toWorkspaceUrl(pathname)}${suffix}`;
    }
    if (pathname === '/department-management') {
      return `${toWorkspaceUrl('/workspace/department-management')}${suffix}`;
    }
    if (pathname === '/auth/staff' || pathname.startsWith('/auth/staff/')) {
      const staffPath = pathname.replace(/^\/auth\/staff/, '/auth') || '/auth';
      return `${toWorkspaceUrl(staffPath)}${suffix}`;
    }
    return null;
  }

  if (isWorkspaceHost()) {
    if (pathname === '/workspace' || pathname.startsWith('/workspace/')) {
      const stripped = workspaceSubdomainPath(pathname);
      if (stripped !== pathname) {
        return `${stripped}${suffix}`;
      }
    }
    if (params.get('mode') === 'register' && (pathname === '/auth' || pathname.startsWith('/auth/'))) {
      return `${toStorefrontUrl(`/auth${suffix}`)}`;
    }
    if (isStorefrontOnlyPath(pathname)) {
      return `${toStorefrontUrl(pathname)}${suffix}`;
    }
  }

  return null;
}

/**
 * Cookie domain for shared auth across storefront + workspace subdomains.
 * Set REACT_APP_AUTH_COOKIE_DOMAIN (e.g. .test.lewouhmart.com) or derive from URL env vars.
 */
export function getSharedAuthCookieDomain(): string | null {
  const explicit = process.env.REACT_APP_AUTH_COOKIE_DOMAIN?.trim();
  if (explicit) {
    return explicit.startsWith('.') ? explicit : `.${explicit}`;
  }

  const host = getHostname();
  if (!host || host === 'localhost' || host === '127.0.0.1') return null;

  for (const origin of [
    process.env.REACT_APP_STOREFRONT_URL,
    process.env.REACT_APP_WORKSPACE_URL,
  ]) {
    if (!origin?.trim()) continue;
    try {
      const h = new URL(origin.trim()).hostname.toLowerCase();
      const parts = h.split('.').filter(Boolean);
      if (parts.length >= 2) {
        return `.${parts.slice(-2).join('.')}`;
      }
    } catch {
      // ignore invalid URL
    }
  }

  const parts = host.split('.').filter(Boolean);
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`;
  }
  return null;
}
